import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toStorageUrl } from '../../utils/storageUrl';
import { extractApiList } from '../../utils/extractApiList';
import { useInView } from '../../hooks/useInView';

/* ─── Icons ─────────────────────────────────────────────────────────── */
const CloseIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="4" y1="4" x2="16" y2="16"/><line x1="16" y1="4" x2="4" y2="16"/>
  </svg>
);
const ZoomInIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="9" r="6"/><line x1="13.5" y1="13.5" x2="18" y2="18"/>
    <line x1="9" y1="6" x2="9" y2="12"/><line x1="6" y1="9" x2="12" y2="9"/>
  </svg>
);
const ZoomOutIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="9" r="6"/><line x1="13.5" y1="13.5" x2="18" y2="18"/>
    <line x1="6" y1="9" x2="12" y2="9"/>
  </svg>
);
const DownloadIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 14v3h14v-3M10 3v9M6 8l4 4 4-4"/>
  </svg>
);
const WarningIcon = () => (
  <svg viewBox="0 0 48 48" width="64" height="64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 6L4 42h40L24 6z" stroke="#ff4444" strokeWidth="2" fill="rgba(255,68,68,0.1)" strokeLinejoin="round"/>
    <line x1="24" y1="20" x2="24" y2="30" stroke="#ff4444" strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="24" cy="36" r="1.5" fill="#ff4444"/>
  </svg>
);
const RetryIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 4v6h6M23 20v-6h-6"/>
    <path d="M20.49 9A9 9 0 005.64 5.64L1 10m18 0l-4.64 4.36A9 9 0 013.51 11"/>
  </svg>
);
const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/>
    <path d="M12 3c-2.5 3-4 5.5-4 9s1.5 6 4 9M12 3c2.5 3 4 5.5 4 9s-1.5 6-4 9M3 12h18"/>
  </svg>
);
const FlagIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
    <line x1="4" y1="22" x2="4" y2="15"/>
  </svg>
);
const FilterIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
  </svg>
);
const TrophyIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 21h8M12 17v4"/>
    <path d="M7 4h10v5a5 5 0 01-10 0V4z"/>
    <path d="M7 4H5a2 2 0 000 4h2M17 4h2a2 2 0 010 4h-2"/>
  </svg>
);
const StarIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);
/* ─── Helpers ────────────────────────────────────────────────────────── */
/* ─── Particle Canvas ─── */
const ParticleCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    let raf;
    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; };
    resize(); window.addEventListener('resize', resize);
    const pts = Array.from({ length: 50 }, () => ({
      x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.32, vy: (Math.random() - 0.5) * 0.32,
      r: Math.random() * 1.4 + 0.4, a: Math.random() * 0.4 + 0.08,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = c.width; if (p.x > c.width) p.x = 0;
        if (p.y < 0) p.y = c.height; if (p.y > c.height) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,68,68,${p.a})`; ctx.fill();
      });
      for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y, d = Math.sqrt(dx*dx+dy*dy);
        if (d < 120) {
          ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y);
          ctx.strokeStyle = `rgba(255,68,68,${0.06*(1-d/120)})`; ctx.lineWidth=0.5; ctx.stroke();
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} style={{position:'fixed',inset:0,zIndex:0,pointerEvents:'none'}} />;
};

const normalizeValue = (v) => String(v ?? '').trim().toLowerCase();

const AnimCounter = ({ target, inView }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let step = 0;
    const steps = 40;
    const t = setInterval(() => {
      step++;
      setVal(Math.round(target * step / steps));
      if (step >= steps) clearInterval(t);
    }, 1200 / steps);
    return () => clearInterval(t);
  }, [inView, target]);
  return <>{val}</>;
};

/* ─── Result Badge ───────────────────────────────────────────────────── */
const ResultBadge = ({ result }) => {
  const low = result.toLowerCase();
  let variant = 'default';
  if (low.includes('1st') || low.includes('first') || low.includes('champion') || low.includes('winner')) variant = 'gold';
  else if (low.includes('2nd') || low.includes('second') || low.includes('runner')) variant = 'silver';
  else if (low.includes('3rd') || low.includes('third')) variant = 'bronze';
  return <span className={`rb rb--${variant}`}><StarIcon />{result}</span>;
};

const SectionHeader = ({ tag, title, subtitle, inView }) => (
  <div className="sh">
    <span className="sh-tag">{tag}</span>
    <h2 className={`sh-title${inView ? ' ul-in' : ''}`}>{title}</h2>
    <div className="sh-bar" />
    {subtitle && <p className="sh-sub">{subtitle}</p>}
  </div>
);

/* ─── Image Preview ──────────────────────────────────────────────────── */
const ImagePreview = ({ image, onClose, onDownload }) => {
  const [zoom, setZoom] = useState(1);
  useEffect(() => setZoom(1), [image]);
  if (!image) return null;
  return (
    <div className="pv-overlay" onClick={onClose}>
      <div className="pv-panel" onClick={(e) => e.stopPropagation()}>
        <div className="pv-top-line" />
        <div className="pv-controls">
          <button type="button" className="pv-btn" onClick={() => setZoom((z) => Math.min(z + 0.15, 3))} title="Zoom In"><ZoomInIcon /></button>
          <button type="button" className="pv-btn" onClick={() => setZoom((z) => Math.max(z - 0.15, 0.5))} title="Zoom Out"><ZoomOutIcon /></button>
          <button type="button" className="pv-btn" onClick={() => onDownload(image)} title="Download"><DownloadIcon /></button>
          <button type="button" className="pv-btn pv-close" onClick={onClose} title="Close"><CloseIcon /></button>
        </div>
        <div className="pv-img-area">
          <img
            src={toStorageUrl(image)}
            alt="Achievement"
            className="pv-img"
            style={{ transform: `scale(${zoom})`, transition: 'transform 0.25s ease' }}
          />
        </div>
        <div className="pv-zoom-tag">ZOOM {Math.round(zoom * 100)}%</div>
      </div>
    </div>
  );
};

/* ─── Achievement Card ───────────────────────────────────────────────── */
const AchCard = ({ achievement, onPreview, index }) => {
  const isIntl = achievement.scope === 'international';
  const logoPath = achievement.competition_logo || achievement.other_pictures?.[0] || null;
  const results = achievement.results || [];

  return (
    <article
      className={`ach-card ach-card--${isIntl ? 'intl' : 'nat'}`}
      style={{ animationDelay: `${index * 0.04}s` }}
    >
      <div className="ach-card-top-line" />
      {/* Scope badge */}
      <div className="ach-scope-badge">
        {isIntl ? <GlobeIcon /> : <FlagIcon />}
        <span>{isIntl ? 'International' : 'National'}</span>
      </div>

      {/* Logo */}
      {logoPath && (
        <div className="ach-logo-wrap">
          <img src={toStorageUrl(logoPath)} alt="" className="ach-logo" />
        </div>
      )}

      {/* Title */}
      <h3 className="ach-title">{achievement.displayName}</h3>

      {/* Results */}
      {results.length > 0 && (
        <ul className="ach-results">
          {results.map((r, i) => (
            <li key={i}><ResultBadge result={r} /></li>
          ))}
        </ul>
      )}

      {/* Team photo */}
      {achievement.representative_picture && (
        <button
          type="button"
          className="ach-photo-btn"
          onClick={() => onPreview(achievement.representative_picture)}
        >
          <img
            src={toStorageUrl(achievement.representative_picture)}
            alt={achievement.displayName}
            className="ach-photo"
          />
          <span className="ach-photo-overlay">
            <ZoomInIcon />
            <span>View team photo</span>
          </span>
        </button>
      )}
      <div className="ach-card-bottom-line" />
    </article>
  );
};

/* ─── Year Section ───────────────────────────────────────────────────── */
const YearSection = ({ yearGroup, filter, onPreview }) => {
  const [ref, inView] = useInView(0.04);
  const filtered = yearGroup.achievements.filter((a) => {
    if (filter === 'national') return a.scope === 'domestic';
    if (filter === 'international') return a.scope === 'international';
    return true;
  });
  if (!filtered.length) return null;

  return (
    <div ref={ref} className={`ys${inView ? ' ys--in' : ''}`}>
      <div className="ys-header">
        <div className="ys-year-block">
          <span className="ys-year">{yearGroup.year}</span>
          <span className="ys-count">{filtered.length} {filtered.length === 1 ? 'record' : 'records'}</span>
        </div>
      </div>
      <div className="ys-grid">
        {filtered.map((ach, i) => (
          <AchCard
            key={`${yearGroup.year}-${ach.id || i}`}
            achievement={ach}
            onPreview={onPreview}
            index={i}
          />
        ))}
      </div>
    </div>
  );
};

/* ─── Skeleton ───────────────────────────────────────────────────────── */
const Skeleton = () => (
  <div className="sk-wrap">
    {[0, 1, 2].map((n) => (
      <div key={n} className="sk-card" style={{ animationDelay: `${n * 0.08}s` }}>
        <div className="sk sk-badge" />
        <div className="sk sk-title" />
        <div className="sk sk-logo" />
        <div className="sk sk-res" />
        <div className="sk sk-photo" />
      </div>
    ))}
  </div>
);

/* ─── Main Component ─────────────────────────────────────────────────── */
const Achievement = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [filter, setFilter] = useState('all'); // all | national | international
  const [yearFilter, setYearFilter] = useState('all');
  const [lastFetchStats, setLastFetchStats] = useState(null);

  const [statsRef, statsInView] = useInView(0.1);
  const [achRef, achInView] = useInView();

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 80);
    fetchAchievements();
  }, []);

  const getDisplayName = (a) => a.event_name || a.event?.name || 'Untitled Achievement';
  const parseAchievements = (desc) => {
    if (!desc) return [];
    return desc.split(/\r?\n/).filter((l) => l.trim().length > 0);
  };

  const fetchAchievements = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/public/achievements', {
        cache: false,
        params: { _t: Date.now() },
      });
      const rows = extractApiList(response.data);
      setLastFetchStats({
        apiRowCount: rows.length,
        publicCount: rows.length,
        achievementEligibleCount: rows.length,
      });

      const publicAchievements = rows;

      const mergedByKey = {};
      publicAchievements.forEach((item) => {
        const year = new Date(item.created_at).getFullYear().toString();
        const displayName = getDisplayName(item);
        const scope = normalizeValue(item.achievement_type) === 'international' ? 'international' : 'domestic';
        const key = `${year}::${scope}::${displayName}::${item.representative_picture || ''}::${item.competition_logo || ''}`;
        if (!mergedByKey[key]) {
          mergedByKey[key] = { ...item, displayName, year, scope, results: [] };
        }
        mergedByKey[key].results.push(...parseAchievements(item.achievement_description));
      });

      const grouped = {};
      Object.values(mergedByKey).forEach((ach) => {
        if (!grouped[ach.year]) grouped[ach.year] = [];
        grouped[ach.year].push(ach);
      });

      const sorted = Object.entries(grouped)
        .sort((a, b) => b[0] - a[0])
        .map(([year, achs]) => ({ year, achievements: achs }));

      setAchievements(sorted);
    } catch (err) {
      console.error('Error fetching achievements:', err);
      if (err.response) setError(`Server error ${err.response.status} — please try again later.`);
      else if (err.request) setError('Unable to connect. Check your internet connection.');
      else setError('Failed to load achievements. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (imagePath) => {
    try {
      const response = await api.get(`/galleries/download/${imagePath}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', imagePath.split('/').pop());
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading image:', err);
    }
  };

  const totalNational = achievements.reduce(
    (s, g) => s + g.achievements.filter((a) => a.scope === 'domestic').length, 0
  );
  const totalIntl = achievements.reduce(
    (s, g) => s + g.achievements.filter((a) => a.scope === 'international').length, 0
  );
  const totalAwards = achievements.reduce(
    (s, g) => s + g.achievements.reduce((acc, a) => acc + (a.results?.length || 0), 0), 0
  );
  const foundingYear = achievements[achievements.length - 1]?.year || '2002';
  const allYears = achievements.map((g) => g.year);

  const displayedGroups = yearFilter === 'all'
    ? achievements
    : achievements.filter((g) => g.year === yearFilter);

  if (loading) return (
    <div className="pg ext-page">
      <ParticleCanvas />
      <div className="grid-bg" /><div className="scanlines" />
      <div className="state-wrap">
        <div className="loading-rig">
          <div className="lr-ring lr-ring-1" />
          <div className="lr-ring lr-ring-2" />
          <div className="lr-ring lr-ring-3" />
          <div className="lr-core" />
        </div>
        <div className="load-tag">// FETCHING_ACHIEVEMENTS</div>
        <h2 className="load-title">LOADING ACHIEVEMENTS</h2>
        <p className="load-sub">Please wait while we retrieve achievement data…</p>
      </div>
      <Styles />
    </div>
  );

  if (error) return (
    <div className="pg ext-page">
      <ParticleCanvas />
      <div className="grid-bg" /><div className="scanlines" />
      <div className="state-wrap">
        <div className="err-icon"><WarningIcon /></div>
        <div className="err-tag">// ERROR_STATE</div>
        <h2 className="err-title">CONNECTION FAILED</h2>
        <p className="err-msg">{error}</p>
        <button type="button" className="retry-btn" onClick={fetchAchievements}>
          <span className="retry-shine" />
          <span className="retry-inner"><RetryIcon /> RETRY CONNECTION</span>
        </button>
      </div>
      <Styles />
    </div>
  );

  return (
    <div className="pg ext-page">
      <div className="grid-bg" aria-hidden="true" />
      <div className="scanlines" aria-hidden="true" />
      <div className="radial-glow" aria-hidden="true" />

      <div className="wrapper">

        <section className={`hero ${isVisible ? 'vis' : ''}`}>
          <div className="hero-tag">
            <span className="ht-dot" />
            <span>UNIVERSITI TEKNOLOGI MALAYSIA // ACHIEVEMENTS</span>
          </div>
          <h1 className="hero-title">
            OUR<br />
            <span className="hero-accent">ACHIEVE<span className="hero-red">MENTS</span></span>
          </h1>
          <div className="hero-divider">
            <div className="hd-l" /><span className="hd-text">COMPETITION_RECORD</span><div className="hd-r" />
          </div>
          <p className="hero-desc">
            National and international robotics victories — honouring UTM Robocon&apos;s champions
            and competitive legacy since <em>{foundingYear}</em>.
          </p>
          <div className="hero-sweep-line"><div className="hsl-inner" /></div>
        </section>

        <section ref={statsRef} className={`sec ${statsInView ? 'sec-in' : ''}`}>
          <div className="sec-shell">
            <div className="glass-card stats-card">
              <div className="card-top-line" />
              <div className="stats-grid">
                {[
                  { label: 'Years Active', val: achievements.length },
                  { label: 'National', val: totalNational },
                  { label: 'International', val: totalIntl },
                  { label: 'Total Awards', val: totalAwards },
                ].map((s, i) => (
                  <div key={i} className="stat-cell">
                    <div className="stat-ring" aria-hidden="true" />
                    <div className="stat-num">
                      {statsInView ? <><AnimCounter target={s.val} inView={statsInView} />+</> : '0+'}
                    </div>
                    <div className="stat-lbl">{s.label}</div>
                    <div className="stat-line" />
                  </div>
                ))}
              </div>
              <div className="card-bottom-line" />
            </div>
          </div>
        </section>

        <section className="filter-sec">
          <div className="filter-bar">
            <div className="filter-group">
              <FilterIcon />
              <span className="filter-label">SCOPE</span>
              {['all', 'national', 'international'].map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`filter-btn${filter === f ? ' filter-btn--active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f === 'all' ? 'ALL' : f === 'national' ? 'NATIONAL' : 'INTERNATIONAL'}
                </button>
              ))}
            </div>
            <div className="filter-divider" />
            <div className="filter-group">
              <span className="filter-label">YEAR</span>
              <select
                className="filter-select"
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
              >
                <option value="all">ALL YEARS</option>
                {allYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className={`sec ${achInView ? 'sec-in' : ''}`} ref={achRef}>
          <div className="sec-shell">
            <SectionHeader
              tag="// CHAMPIONSHIP_TIMELINE"
              title="COMPETITION RECORD"
              subtitle={`National Robocon Malaysia and international ABU Robocon results since ${foundingYear}`}
              inView={achInView}
            />

            {achievements.length === 0 ? (
              <div className="glass-card empty-card">
                <div className="card-top-line" />
                <TrophyIcon />
                <div className="empty-tag">// NO_RECORDS_FOUND</div>
                <h3 className="empty-title">No public achievements yet</h3>
                <p className="empty-desc">
                  When galleries are marked public and saved as achievements, they will appear here.
                </p>
                {lastFetchStats && (
                  <p className="debug-fetch-hint" role="status">
                    API returned {lastFetchStats.apiRowCount} row(s); {lastFetchStats.achievementEligibleCount} eligible.
                  </p>
                )}
                <div className="card-bottom-line" />
              </div>
            ) : (
              displayedGroups.map((yg) => (
                <YearSection
                  key={yg.year}
                  yearGroup={yg}
                  filter={filter}
                  onPreview={setPreviewImage}
                />
              ))
            )}

          </div>
        </section>

        <section className="sec sec-in">
          <div className="sec-shell">
            <div className="glass-card legacy-card">
              <div className="card-top-line" />
              <div className="legacy-tag">// LEGACY_ONGOING</div>
              <h2 className="legacy-title">Champions in the making</h2>
              <p className="legacy-desc">
                Since <em>{foundingYear}</em>, UTM Robocon has represented Malaysia with pride — winning on national
                stages and competing internationally. Every result reflects teamwork, discipline, and engineering excellence.
              </p>
              <div className="card-bottom-line" />
            </div>
          </div>
        </section>
      </div>

      <ImagePreview image={previewImage} onClose={() => setPreviewImage(null)} onDownload={handleDownload} />
      <Styles />
    </div>
  );
};

/* ─── Styles ─────────────────────────────────────────────────────────── */
const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600;700;900&family=Rajdhani:wght@300;400;500;600;700&family=Share+Tech+Mono&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

    .pg{position:relative;min-height:100vh;background:#060608;font-family:'Rajdhani',-apple-system,BlinkMacSystemFont,sans-serif;color:#e0e0e0;overflow-x:hidden;}
    .grid-bg{position:fixed;inset:0;z-index:1;pointer-events:none;background-image:linear-gradient(rgba(255,68,68,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,68,68,0.03) 1px,transparent 1px);background-size:72px 72px;}
    .scanlines{position:fixed;inset:0;z-index:2;pointer-events:none;background:repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.05) 2px,rgba(0,0,0,0.05) 4px);}
    .radial-glow{position:fixed;inset:0;z-index:1;pointer-events:none;background:radial-gradient(ellipse at 20% 80%,rgba(255,34,34,0.06),transparent 55%),radial-gradient(ellipse at 80% 20%,rgba(255,34,34,0.04),transparent 55%);}

    .hero{padding:6.5rem 0 4.5rem;text-align:center;opacity:0;transform:translateY(26px);transition:opacity 0.9s ease,transform 0.9s ease;}
    .hero.vis{opacity:1;transform:none;}
    .hero-tag{display:inline-flex;align-items:center;gap:8px;background:rgba(255,68,68,0.07);border:1px solid rgba(255,68,68,0.2);border-radius:2px;padding:5px 14px;margin-bottom:1.8rem;font-family:'Share Tech Mono',monospace;font-size:0.62rem;color:#ff7766;letter-spacing:1.5px;}
    .ht-dot{width:7px;height:7px;border-radius:50%;background:#ff4444;box-shadow:0 0 8px #ff4444;animation:blink 1.6s ease-in-out infinite;}
    @keyframes blink{0%,100%{opacity:1;box-shadow:0 0 8px #ff4444}50%{opacity:0.3;box-shadow:0 0 3px #ff4444}}
    .hero-title{font-family:'Orbitron',sans-serif;font-size:clamp(2.8rem,7vw,5.5rem);font-weight:900;color:#fff;line-height:0.9;margin-bottom:1.6rem;}
    .hero-accent{display:block;}
    .hero-red{color:#ff2222;text-shadow:0 0 18px rgba(255,34,34,0.7),0 0 40px rgba(255,34,34,0.35);animation:glow-pulse 3s ease-in-out infinite;}
    @keyframes glow-pulse{0%,100%{text-shadow:0 0 18px rgba(255,34,34,0.7),0 0 40px rgba(255,34,34,0.35)}50%{text-shadow:0 0 28px rgba(255,34,34,0.95),0 0 60px rgba(255,34,34,0.55)}}
    .hero-divider{display:flex;align-items:center;gap:1rem;margin:0 auto 1.5rem;max-width:460px;}
    .hd-l{flex:1;height:1px;background:linear-gradient(90deg,transparent,#ff4444);}
    .hd-r{flex:1;height:1px;background:linear-gradient(90deg,#ff4444,transparent);}
    .hd-text{font-family:'Share Tech Mono',monospace;font-size:0.6rem;color:#ff6644;letter-spacing:3px;white-space:nowrap;}
    .hero-desc{font-size:1.1rem;color:#8a97a8;line-height:1.75;max-width:620px;margin:0 auto 1.6rem;}
    .hero-desc em{color:#ff8877;font-style:normal;font-weight:600;}
    .hero-sweep-line{width:120px;height:2px;background:rgba(255,68,68,0.18);border-radius:1px;overflow:hidden;position:relative;margin:0 auto;}
    .hsl-inner{position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg,transparent,#ff4444,transparent);animation:sweep-ln 2.5s ease-in-out infinite;}
    @keyframes sweep-ln{0%{left:-100%}100%{left:200%}}

    .glass-card{background:rgba(8,8,14,0.86);border:1px solid rgba(255,68,68,0.12);border-radius:5px;position:relative;overflow:hidden;backdrop-filter:blur(18px);}
    .card-top-line{position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(255,68,68,0.65),transparent);pointer-events:none;}
    .card-bottom-line{position:absolute;bottom:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(255,68,68,0.2),transparent);pointer-events:none;}

    .stats-card{padding:2.5rem;width:100%;}
    .stats-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:1.5rem;}
    .stat-cell{text-align:center;padding:1.5rem 1rem;background:rgba(255,68,68,0.04);border:1px solid rgba(255,68,68,0.1);border-radius:4px;position:relative;overflow:hidden;}
    .stat-ring{position:absolute;top:-20px;right:-20px;width:60px;height:60px;border-radius:50%;border:1px solid rgba(255,68,68,0.1);pointer-events:none;}
    .stat-num{font-family:'Orbitron',sans-serif;font-size:2rem;font-weight:900;color:#ff3333;text-shadow:0 0 12px rgba(255,51,51,0.5);margin-bottom:0.4rem;}
    .stat-lbl{font-family:'Share Tech Mono',monospace;font-size:0.58rem;color:#7a8a9a;letter-spacing:1px;text-transform:uppercase;}
    .stat-line{position:absolute;bottom:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,#ff4444,transparent);animation:stat-ln 3s ease-in-out infinite;}
    @keyframes stat-ln{0%,100%{opacity:0}50%{opacity:0.7}}

    .sec-loading-tag{font-family:'Share Tech Mono',monospace;font-size:0.62rem;color:#ff6644;letter-spacing:1.8px;margin-bottom:1.2rem;text-align:center;}

    .ys{margin-bottom:3.5rem;text-align:center;opacity:1;transform:none;}
    .ys--in{animation:ys-in 0.5s ease both;}
    @keyframes ys-in{to{opacity:1;transform:none}}
    .ys-header{display:flex;flex-direction:column;align-items:center;gap:0.35rem;margin-bottom:1.6rem;}
    .ys-year-block{display:flex;flex-direction:column;align-items:center;gap:0.25rem;}
    .ys-year{font-family:'Orbitron',sans-serif;font-size:2.4rem;font-weight:900;color:#ff2222;letter-spacing:2px;line-height:1;}
    .ys-count{font-family:'Share Tech Mono',monospace;font-size:0.6rem;color:#7a8a9a;letter-spacing:1.5px;text-transform:uppercase;}
    .ys-grid{display:flex;flex-wrap:wrap;gap:1.5rem;justify-content:center;max-width:1100px;margin:0 auto;}

    .ach-card{position:relative;width:100%;max-width:340px;background:rgba(8,8,14,0.86);border-radius:5px;padding:1.4rem;text-align:left;overflow:hidden;transition:transform 0.3s,border-color 0.3s,box-shadow 0.3s;animation:card-up 0.45s ease both;}
    @keyframes card-up{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
    .ach-card:hover{transform:translateY(-5px);}
    .ach-card--nat{border:1px solid rgba(255,68,68,0.2);}
    .ach-card--nat:hover{border-color:rgba(255,68,68,0.4);box-shadow:0 10px 36px rgba(255,68,68,0.12);}
    .ach-card--intl{border:1px solid rgba(255,68,68,0.12);}
    .ach-card--intl:hover{border-color:rgba(255,68,68,0.28);box-shadow:0 10px 32px rgba(255,68,68,0.08);}
    .ach-card-top-line,.ach-card-bottom-line{position:absolute;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(255,68,68,0.45),transparent);pointer-events:none;}
    .ach-card-top-line{top:0;}
    .ach-card-bottom-line{bottom:0;opacity:0.5;}

    .ach-scope-badge{display:inline-flex;align-items:center;gap:6px;margin-bottom:0.8rem;font-family:'Share Tech Mono',monospace;font-size:0.58rem;letter-spacing:1px;padding:3px 10px;border-radius:2px;text-transform:uppercase;}
    .ach-card--nat .ach-scope-badge{background:rgba(255,68,68,0.1);color:#ff7766;border:1px solid rgba(255,68,68,0.28);}
    .ach-card--intl .ach-scope-badge{background:rgba(255,255,255,0.04);color:#b0bfd0;border:1px solid rgba(255,255,255,0.14);}

    .ach-logo-wrap{margin-bottom:0.9rem;text-align:center;}
    .ach-logo{max-height:60px;max-width:150px;object-fit:contain;}
    .ach-title{font-family:'Rajdhani',sans-serif;font-size:1.05rem;font-weight:700;color:#fff;line-height:1.35;margin-bottom:0.85rem;}
    .ach-results{list-style:none;display:flex;flex-direction:column;gap:0.4rem;margin-bottom:1rem;}
    .rb{display:inline-flex;align-items:center;gap:5px;font-family:'Share Tech Mono',monospace;font-size:0.58rem;font-weight:700;letter-spacing:0.8px;padding:4px 10px;border-radius:2px;}
    .rb--gold{background:rgba(255,215,0,0.13);border:1px solid rgba(255,215,0,0.4);color:#FFD700;}
    .rb--silver{background:rgba(192,192,192,0.1);border:1px solid rgba(192,192,192,0.35);color:#C0C0C0;}
    .rb--bronze{background:rgba(205,127,50,0.13);border:1px solid rgba(205,127,50,0.4);color:#CD7F32;}
    .rb--default{background:rgba(255,68,68,0.07);border:1px solid rgba(255,68,68,0.22);color:#ff8866;}

    .ach-photo-btn{display:block;width:100%;padding:0;border:none;border-radius:3px;overflow:hidden;cursor:pointer;position:relative;background:#06060e;border:1px solid rgba(255,68,68,0.12);margin-top:0.4rem;}
    .ach-photo-btn:hover{border-color:rgba(255,68,68,0.35);}
    .ach-photo{width:100%;height:160px;object-fit:cover;display:block;transition:transform 0.4s ease,filter 0.4s ease;}
    .ach-photo-btn:hover .ach-photo{transform:scale(1.04);filter:brightness(0.75);}
    .ach-photo-overlay{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;font-family:'Share Tech Mono',monospace;font-size:0.58rem;letter-spacing:1.5px;color:#ff6644;opacity:0;transition:opacity 0.3s;}
    .ach-photo-btn:hover .ach-photo-overlay{opacity:1;}

    .empty-card{padding:4rem 3rem;max-width:580px;margin:0 auto;text-align:center;}
    .empty-card svg{margin-bottom:1.2rem;opacity:0.4;}
    .empty-tag{font-family:'Share Tech Mono',monospace;font-size:0.6rem;color:#555;letter-spacing:1.5px;margin-bottom:0.6rem;}
    .empty-title{font-family:'Orbitron',sans-serif;font-size:1rem;font-weight:700;color:#fff;letter-spacing:2px;margin-bottom:0.8rem;}
    .empty-desc{font-size:0.9rem;color:#7a8a9a;line-height:1.65;}
    .debug-fetch-hint{margin-top:1.25rem;font-family:'Share Tech Mono',monospace;font-size:0.58rem;color:#5a6670;line-height:1.55;}

    .legacy-card{padding:3.5rem 2.5rem;text-align:center;width:100%;}
    .legacy-tag{font-family:'Share Tech Mono',monospace;font-size:0.6rem;color:#ff6644;letter-spacing:2px;margin-bottom:1rem;}
    .legacy-title{font-family:'Orbitron',sans-serif;font-size:clamp(1.4rem,3vw,2rem);font-weight:900;color:#fff;letter-spacing:3px;margin-bottom:1rem;}
    .legacy-desc{font-size:1rem;color:#7a8a9a;line-height:1.75;max-width:560px;margin:0 auto;}
    .legacy-desc em{color:#ff8877;font-style:normal;font-weight:600;}

    .state-wrap{position:relative;z-index:3;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:2rem;text-align:center;}
    .loading-rig{position:relative;width:90px;height:90px;margin:0 auto 2.5rem;}
    .lr-ring{position:absolute;border-radius:50%;border-style:solid;border-color:transparent;}
    .lr-ring-1{inset:0;border-width:2px;border-top-color:#ff4444;animation:spin 1.2s linear infinite;box-shadow:0 0 12px rgba(255,68,68,0.4);}
    .lr-ring-2{inset:10px;border-width:2px;border-right-color:rgba(255,68,68,0.6);animation:spin 2s linear infinite reverse;}
    .lr-ring-3{inset:20px;border-width:1px;border-bottom-color:rgba(255,68,68,0.35);animation:spin 3s linear infinite;}
    @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
    .lr-core{position:absolute;inset:36px;border-radius:50%;background:#ff4444;box-shadow:0 0 14px #ff4444;animation:blink 1.2s ease-in-out infinite;}
    .load-tag{font-family:'Share Tech Mono',monospace;font-size:0.62rem;color:#555;letter-spacing:2px;margin-bottom:0.8rem;}
    .load-title{font-family:'Orbitron',sans-serif;font-size:1.4rem;font-weight:700;color:#fff;letter-spacing:4px;margin-bottom:0.6rem;}
    .load-sub{font-size:0.9rem;color:#7a8a9a;}
    .err-icon{margin:0 auto 1.5rem;animation:warn-pulse 2s ease-in-out infinite;}
    @keyframes warn-pulse{0%,100%{filter:drop-shadow(0 0 8px rgba(255,68,68,0.4))}50%{filter:drop-shadow(0 0 18px rgba(255,68,68,0.7))}}
    .err-tag{font-family:'Share Tech Mono',monospace;font-size:0.62rem;color:#555;letter-spacing:2px;margin-bottom:0.8rem;}
    .err-title{font-family:'Orbitron',sans-serif;font-size:1.4rem;font-weight:700;color:#fff;letter-spacing:4px;margin-bottom:0.8rem;}
    .err-msg{font-size:0.95rem;color:#7a8a9a;margin-bottom:2rem;max-width:440px;line-height:1.6;}
    .retry-btn{position:relative;overflow:hidden;background:#cc1111;border:none;border-radius:3px;cursor:pointer;padding:0;font-family:'Orbitron',sans-serif;animation:btn-pulse 2.5s ease-in-out infinite;}
    @keyframes btn-pulse{0%,100%{box-shadow:0 0 14px rgba(255,34,34,0.3)}50%{box-shadow:0 0 28px rgba(255,34,34,0.6)}}
    .retry-shine{position:absolute;top:0;left:-80%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent);transition:left 0.5s ease;}
    .retry-btn:hover .retry-shine{left:130%;}
    .retry-inner{position:relative;z-index:1;display:flex;align-items:center;gap:8px;padding:0.9rem 2rem;font-size:0.7rem;font-weight:700;color:#fff;letter-spacing:2px;}
    .retry-btn:hover{transform:translateY(-2px);box-shadow:0 0 28px rgba(255,34,34,0.5);}

    .pv-overlay{position:fixed;inset:0;z-index:20000;background:rgba(2,2,6,0.92);backdrop-filter:blur(12px);display:flex;align-items:center;justify-content:center;padding-top:86px;animation:fade-in 0.25s ease;}
    @keyframes fade-in{from{opacity:0}to{opacity:1}}
    .pv-panel{position:relative;background:rgba(8,8,16,0.98);border:1px solid rgba(255,68,68,0.2);border-radius:5px;max-width:90vw;max-height:92vh;overflow:hidden;box-shadow:0 0 48px rgba(255,34,34,0.15);}
    .pv-top-line{position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(255,68,68,0.7),transparent);z-index:2;}
    .pv-controls{position:absolute;top:0.8rem;right:0.8rem;display:flex;flex-direction:column;gap:0.4rem;z-index:3;}
    .pv-btn{width:36px;height:36px;background:rgba(6,6,12,0.92);border:1px solid rgba(255,68,68,0.2);border-radius:3px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#8a97a8;transition:all 0.2s;}
    .pv-btn:hover{background:rgba(255,68,68,0.12);border-color:rgba(255,68,68,0.5);color:#ff4444;}
    .pv-img-area{overflow:auto;max-height:88vh;display:flex;align-items:center;justify-content:center;padding:1rem;}
    .pv-img{display:block;max-width:100%;height:auto;}
    .pv-zoom-tag{position:absolute;bottom:0.6rem;left:1rem;font-family:'Share Tech Mono',monospace;font-size:0.58rem;color:#555;letter-spacing:1px;}

    .sk-wrap{display:flex;flex-wrap:wrap;gap:1.5rem;justify-content:center;max-width:1100px;margin:0 auto;}
    .sk-card{width:100%;max-width:340px;background:rgba(8,8,14,0.86);border:1px solid rgba(255,68,68,0.1);border-radius:5px;padding:1.4rem;animation:card-up 0.4s ease both;}
    .sk{display:block;border-radius:3px;background:linear-gradient(90deg,rgba(255,68,68,0.08) 25%,rgba(255,68,68,0.18) 50%,rgba(255,68,68,0.08) 75%);background-size:220% 100%;animation:shimmer 1.2s ease-in-out infinite;}
    @keyframes shimmer{0%{background-position:200% 0}100%{background-position:-20% 0}}
    .sk-badge{width:110px;height:20px;margin-bottom:0.9rem;}
    .sk-title{width:80%;height:18px;margin-bottom:0.75rem;}
    .sk-logo{width:130px;height:52px;margin:0 auto 0.75rem;}
    .sk-res{width:100%;height:22px;margin-bottom:0.75rem;}
    .sk-photo{width:100%;height:148px;}
    .sk-stat{width:62px;height:28px;margin:0 auto;}

    @media(max-width:900px){.stats-grid{grid-template-columns:repeat(2,1fr);}}
    @media(max-width:768px){
      .hero{padding:5rem 0 3rem;}
      .stats-card,.legacy-card{padding:2rem 1.5rem;}
      .ys-year{font-size:1.8rem;}
      .ach-card{max-width:100%;}
      .sk-card{max-width:100%;}
    }
    @media(max-width:480px){
      .stats-grid{grid-template-columns:1fr 1fr;}
    }
  `}</style>
);

export default Achievement;